<ng-container *ngIf="status === 'loading'">
  <ng-container *ngTemplateOutlet="loader"></ng-container>
</ng-container>
<ng-container *ngIf="status === 'idle'">
  <ng-container *ngTemplateOutlet="content"></ng-container>
</ng-container>
<ng-container *ngIf="status === 'error'">
  <ng-container *ngTemplateOutlet="error"></ng-container>
</ng-container>

<ng-template #content>
  <section class="content-wrapper" [ngClass]="{'v2': data.isV2}">
    <header>
      <h3 class="typography-h6 dark-text">{{ 'billing.invoice.upcomingPreview' | translate }}</h3>
      <mat-icon *ngIf="!data.isV2" class="close-icon" mat-dialog-close>close</mat-icon>
    </header>

    <div class="row-group">
      <!-- Non-V2 version: Billing period and date on separate lines -->
      <ng-container *ngIf="!data.isV2">
        <h6 class="col-item">{{ 'billing.invoice.billingPeriod' | translate }}</h6>
        <span class="col-item">
          {{upcomingInvoiceData.billingCycleStart | date}}
          {{ 'common.to' | translate | lowercase }}
          {{upcomingInvoiceData.billingCycleEnd | date}}
        </span>
      </ng-container>

      <!-- V2 version: Billing period and date on the same line -->
      <ng-container *ngIf="data.isV2">
        <h6 class="col-item billing-period-v2">
          <span class="period-label dark-text">{{ 'billing.invoice.billingPeriod' | translate }}:</span>
          <span class="period-date dark-text">{{upcomingInvoiceData.billingCycleStart | date}}
          {{ 'common.to' | translate | lowercase }} {{upcomingInvoiceData.billingCycleEnd | date}}</span>
        </h6>
      </ng-container>
    </div>

    <mat-table matSort [dataSource]="dataSource" class="invoice-table" [ngClass]="{'dark-text': data.isV2}" #table="matTable">
      <!-- Product Column -->
      <ng-container matColumnDef="product">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'billing.invoice.product' | translate }}
        </mat-header-cell>
        <mat-cell *matCellDef="let invoice;table: table">
          <h5 class="cell-title">{{ 'billing.invoice.product' | translate }}</h5>
          <div>
            <span>{{ invoice.product | translate }}</span>
            <span *ngIf="invoice.startDate && invoice.endDate">
              {{invoice.startDate | date}}
              {{ 'common.to' | translate | lowercase }}
              {{invoice.endDate | date}}
            </span>
          </div>
        </mat-cell>
      </ng-container>

      <!-- Quantity Column -->
      <ng-container matColumnDef="quantity">
        <mat-header-cell class="border" *matHeaderCellDef mat-sort-header>{{ 'billing.invoice.quantity' | translate }}
        </mat-header-cell>
        <mat-cell *matCellDef="let invoice;table: table">
          <h5 class="cell-title">{{ 'billing.invoice.quantity' | translate }}</h5>
          <span>{{invoice.quantity}}</span>
        </mat-cell>
      </ng-container>

      <!-- Unit price Column -->
      <ng-container matColumnDef="unitPrice">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'billing.invoice.unitPrice' | translate | capitalize}}
        </mat-header-cell>
        <mat-cell *matCellDef="let invoice;table: table">
          <h5 class="cell-title">{{ 'billing.invoice.unitPrice' | translate }}</h5>
          <span>{{formatLocalizedPrice(invoice.unitPrice)}}</span>
        </mat-cell>
      </ng-container>

      <!-- Amount Column -->
      <ng-container matColumnDef="amount">
        <mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'billing.amount' | translate }}
        </mat-header-cell>
        <mat-cell *matCellDef="let invoice;table: table">
          <h5 class="cell-title">{{ 'billing.amount' | translate }}</h5>
          <span>{{formatLocalizedPrice(invoice.amount)}}</span>
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></mat-header-row>
      <mat-row *matRowDef="let row; columns: displayedColumns;"></mat-row>
    </mat-table>

    <div class="row-group row-group-divider with-top-divider" [ngClass]="{'dark-text': data.isV2}">
      <h6 class="col-item">{{ 'billing.disc' | translate }}</h6>
      <span class="col-item totals">
        {{formatLocalizedDiscount(+upcomingInvoiceData.totals.discount)}}
      </span>
    </div>
    <div class="row-group row-group-totals" [ngClass]="{'dark-text': data.isV2}">
      <h6 class="col-item">{{ 'billing.tax' | translate }}</h6>
      <span class="col-item totals">{{formatLocalizedPrice(+upcomingInvoiceData.tax)}}</span>
    </div>
    <div class="row-group row-group-totals" [ngClass]="{'dark-text': data.isV2}" *ngIf="+upcomingInvoiceData.totals?.credit > 0">
      <h6 class="col-item">{{ 'billing.credit' | translate }}</h6>
      <span class="col-item credit">{{formatLocalizedPrice(+upcomingInvoiceData.totals?.credit * -1)}}</span>
    </div>
    <div class="row-group row-group-totals" [ngClass]="{'dark-text': data.isV2}">
      <h6 class="col-item total-label">{{ ('billing.total' | translate) + '*' }}</h6>
      <span class="col-item totals final-total-label">{{formatLocalizedPrice(+upcomingInvoiceData.totals.grandTotal)}}</span>
    </div>

    <footer>
      <p class="typography-caption" *ngIf="!data.isV2">
        {{ 'billing.invoice.disclaimerForTotal' | translate: { recurrence: disclaimerRecurrence | translate } }}
      </p>
      <p class="typography-caption v2-disclaimer" *ngIf="data.isV2">
        {{ 'billing.invoice.disclaimerForTotalV2' | translate }}
      </p>
      <button *ngIf="data.isV2" mat-raised-button color="primary" class="close-button" (click)="dialogRef.close()">
        {{ 'common.close' | translate | uppercase }}
      </button>
    </footer>
  </section>
</ng-template>

<ng-template #loader>
  <div class="skeleton-loader" [ngClass]="{'v2': data.isV2}">
    <span class="skeleton-item" style="height: 40px; width: 50%"></span>
    <div fxLayout="column" fxLayoutGap="8px">
      <span *ngFor="let item of skeletonItems" class="skeleton-item"></span>
    </div>
    <span class="skeleton-item" [style.height]="'48px'"></span>
  </div>
</ng-template>

<ng-template #error>
  <div class="error-wrapper" [ngClass]="{'v2': data.isV2}">
    <mat-icon *ngIf="!data.isV2" class="close-icon" mat-dialog-close>close</mat-icon>
    <app-empty-state>
      <span title>{{ errorTitle | translate }}</span>
      <span description>{{ errorDesc | translate }}</span>
    </app-empty-state>
    <button *ngIf="data.isV2" mat-raised-button color="primary" class="close-button" (click)="dialogRef.close()">
      {{ 'common.close' | translate }}
    </button>
  </div>
</ng-template>
